import ConversationApi from '../../api/inbox/conversation';
import { emitter } from 'shared/helpers/mitt';

// Ten to twenty dashboards can be connected at once. Keep the badge live while
// collapsing message bursts before every visible client reaches the count API.
const COALESCE_MS = 1000;
const MAX_WAIT_MS = 3000;
const pendingByFingerprint = new Map();

const state = {
  count: 0,
  generation: null,
  // Generation at the moment the count was last committed. Drift means "the
  // count changed although NO reply-state event was observed since the last
  // commit" — a genuinely missed event. Without this anchor the forced safety
  // check manufactures its own drift whenever an event's coalesced fetch is
  // still in flight, causing causeless full list resets every few minutes
  // (refresh-storm review, 2026-07-11).
  committedGeneration: null,
  activeFingerprint: null,
  latestRequestId: 0,
  updatedAt: null,
};

export const getters = {
  getStats: $state => $state,
  getCount: $state => $state.count,
};

// `force` is a freshness flag, not a scope — a forced safety fetch and a
// normal event fetch for the same filters must share one identity, or each
// invalidates the other's in-flight response (stale stored count → fake drift).
const fingerprintFor = payload => {
  const { force, ...scope } = payload || {};
  return JSON.stringify(scope);
};

const schedule = (dispatch, payload) => {
  const fingerprint = fingerprintFor(payload);
  const existing = pendingByFingerprint.get(fingerprint) || {};
  existing.payload = payload;
  clearTimeout(existing.timer);
  existing.timer = setTimeout(() => {
    clearTimeout(existing.maxTimer);
    pendingByFingerprint.delete(fingerprint);
    dispatch('fetchNow', existing.payload);
  }, COALESCE_MS);
  if (!existing.maxTimer) {
    existing.maxTimer = setTimeout(() => {
      clearTimeout(existing.timer);
      pendingByFingerprint.delete(fingerprint);
      dispatch('fetchNow', existing.payload);
    }, MAX_WAIT_MS);
  }
  pendingByFingerprint.set(fingerprint, existing);
};

export const actions = {
  get({ commit, dispatch }, payload) {
    commit('SET_ACTIVE_FINGERPRINT', fingerprintFor(payload));
    schedule(dispatch, payload);
  },

  async fetchNow({ commit, state: $state }, payload) {
    const fingerprint = fingerprintFor(payload);
    const requestId = $state.latestRequestId + 1;
    const generationAtStart = $state.generation;
    commit('REQUEST_STARTED', { requestId });
    try {
      const { data } = await ConversationApi.needsReplyCount(payload);
      if (requestId !== $state.latestRequestId) return;
      if (fingerprint !== $state.activeFingerprint) return;
      if ($state.generation !== generationAtStart) return;

      const countChangedDuringForcedRevalidation =
        payload.force &&
        $state.updatedAt &&
        Number(data.needs_reply_count) !== $state.count &&
        // Only a genuinely missed event counts as drift: if any reply-state
        // event was observed since the last committed count, the change is
        // those events (their row payloads already converged the list).
        generationAtStart === $state.committedGeneration;
      commit('SET_COUNT', {
        count: data.needs_reply_count,
        generation: data.generation,
      });
      if (countChangedDuringForcedRevalidation) {
        emitter.emit('needs_reply_count_drift');
      }
    } catch (error) {
      // Keep the last known count. Reconnect/focus and the visible-tab safety
      // refresh provide bounded retry opportunities while the app is online.
    }
  },

  observeGeneration({ commit }, generation) {
    if (generation) commit('SET_GENERATION', generation);
  },
};

export const mutations = {
  REQUEST_STARTED($state, { requestId }) {
    $state.latestRequestId = requestId;
  },
  SET_ACTIVE_FINGERPRINT($state, fingerprint) {
    $state.activeFingerprint = fingerprint;
  },
  SET_COUNT($state, { count, generation }) {
    $state.count = Number(count) || 0;
    $state.generation = generation || $state.generation;
    $state.committedGeneration = $state.generation;
    $state.updatedAt = new Date();
  },
  SET_GENERATION($state, generation) {
    $state.generation = generation;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
